import { useState } from 'react';
import { Box, Typography, Popover, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Small, low-key info icon that opens a popover explaining the numbers
export function InfoButton({ intro, items = [] }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <Box
        component="span"
        role="button"
        aria-label="What do these numbers mean?"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          cursor: 'pointer',
          color: theme.palette.text.disabled,
          opacity: 0.6,
          transition: 'opacity 0.2s, color 0.2s',
          '&:hover': { opacity: 1, color: theme.palette.primary.main },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 15 }} />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 420,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          },
        }}
      >
        {intro && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: items.length ? 1.5 : 0 }}>
            {intro}
          </Typography>
        )}
        {items.map((item) => (
          <Box key={item.label} sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
}

// Table title with the info icon right after it
export function TableInfoTitle({ title, info }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Typography
        variant="h6"
        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {title}
      </Typography>
      <InfoButton {...info} />
    </Box>
  );
}

// Wraps a card or chart and puts the info icon in its top-right corner
export function InfoCorner({ info, children }) {
  return (
    <Box sx={{ position: 'relative' }}>
      {children}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <InfoButton {...info} />
      </Box>
    </Box>
  );
}
